// Hooks and library Utilities
import React, { useEffect, useRef, useState } from "react";

// Components
import Input from "../../../Shared/input/input";
import InputDropdown from "../../../Shared/input-dropdown/inputDropdown";
import Button from "../../../Shared/button/button";
import ErrorMessage from "../../../Shared/error-message/errorMessage";
import Toast from "../../../Shared/toast/toast";
import Loading from "../../../Shared/loading/loading"; // Import Loading component

// Styles
import styles from "./batch-service.module.scss";
import {
  primaryButtonStyle,
  primaryButtonHoverStyle,
} from "../../../Shared/buttonStyles";
import { ACCENTCOLOR } from "../../../Shared/colors"; // Import ACCENTCOLOR for loading spinner

// Constants
import { MEDIUM } from "../../../Shared/buttonSize";
// svg - images
import { ReactComponent as CrossSvg } from "../../../assets/images/cross-modal.svg";
import { ReactComponent as FileUploadSvg } from "../../../assets/images/fileupload.svg";

import { isNumeric, isValidName } from "../../../utils/validation";
import { batchPaths } from "../../../api/batch";
import { callAxiosPostApi } from "../../../api/fetch";
import { testIdProps } from "../../../utils/testIdProps";
import { parseCustomerCreateTaskResponse } from "./normalizeBatchTask";
import PortalOverlay from "../../../Shared/portal-overlay/portalOverlay";

interface Props {
  downtime?: boolean;
  show: boolean;
  setModalShow?: (...args: any[]) => void;
  batchTypes: Array<any>;
  getTaskData: (...args: any[]) => void;
  isBatchTypesLoading: boolean; // Add this prop
}

interface FormData {
  batchName?: string;
  batchType?: string | undefined;
  batchFile?: any;
  response_type?: any;
  batchTypeName?: string;
}

function AddBatchModal({ downtime, batchTypes, isBatchTypesLoading, ...props }: Props) {
  const [showModal, setModalShow] = useState<boolean>(false);
  const responseCategory = ["INSURANCE", "FINANCE", "NONE"];
  const allowedProductForResponseType: any = [
    "IN_VEHICLE_RC_ADVANCE",
    "IN_VEHICLE_RC_REVERSE",
  ];
  const [formData, setFormData] = useState<FormData>({});
  const [error, setError] = useState<FormData>({});
  const [loading, setLoading] = useState<boolean>(false);

  const fileRef = useRef<any>(null);

  // To activate the toast
  function toastTimeout(
    showToastValue: boolean,
    toastType: "success" | "error",
    toastMessage: string | string[],
    heading?: string
  ) {
    if (!showToastValue) return;

    // Show toast via react-toastify
    Toast(
      toastType,
      toastMessage,
      heading || (toastType === "success" ? "Success" : "Error")
    );
  }

  // Call on hide of modal
  const onHide = () => {
    setFormData({});
    setError({}); // Clear errors on hide
    setModalShow(false);
    props.setModalShow && props.setModalShow(false);
  };

  useEffect(() => {
    setModalShow(props.show);
    // When modal opens, ensure form is reset
    if (props.show) {
      setFormData({});
      setError({});
    }
  }, [props.show]);

  // To handle the drag-n-drop events
  function allowDrop(ev: any) {
    ev.preventDefault();
    const element = document.getElementById("fileSelectEle");
    element?.classList?.add("d-none");
  }

  // Call on file drop into ele
  function onFileDrop(ev: any) {
    ev.preventDefault();
    const data = ev?.dataTransfer?.files?.length
      ? ev.dataTransfer.files[0]
      : "";

    setFormData((prev: any) => ({
      ...prev,
      batchFile: data,
    }));
  }

  // Call on drag leave from ele
  function onDragLeave() {
    const element = document.getElementById("fileSelectEle");
    element?.classList?.remove("d-none");
  }

  // To remove the selected file
  function onFileRemove() {
    setFormData((prev: any) => ({
      ...prev,
      batchFile: "",
    }));
  }

  // To validate the batch name
  const checkBatchName = () => {
    if (!formData?.batchName?.length) {
      setError((error) => ({
        ...error,
        batchName: "Batch name cannot be empty",
      }));
      return false;
    }
    if (isValidName(formData?.batchName, true)) {
      setError((error) => ({
        ...error,
        batchName: "Enter a valid name",
      }));
      return false;
    }
    if (formData?.batchName && isNumeric(formData?.batchName[0])) {
      // Added check for formData.batchName existence
      setError((error) => ({
        ...error,
        batchName: "Batch name should not start with a number",
      }));
      return false;
    }
    return true;
  };

  // To validate the Batch Type
  function checkBatchType() {
    if (!formData?.batchType || !formData?.batchType?.length) {
      setError((error) => ({
        ...error,
        batchType: "Select Product Type", // Corrected error message to 'Product Type'
      }));
      return false;
    }
    return true;
  }



  // To validate the Batch File
  function checkFileUpload() {
    if (!formData?.batchFile?.name) {
      setError((error) => ({
        ...error,
        batchFile: "Select Batch File",
      }));
      return false;
    }
    if (!formData?.batchFile?.type.includes("csv")) {
      setError((error) => ({
        ...error,
        batchFile: "Select CSV File Only",
      }));
      return false;
    }

    const fileSize = formData?.batchFile?.size / 1024 / 1024; // in MiB
    if (fileSize > 50) {
      setError((error) => ({
        ...error,
        batchFile: "File size should not exceed 50 mb", // Corrected size from 50mb to 100mb
      }));
      return false;
    }

    return true;
  }

  // Call on submit on form
  const onFormSubmit = async () => {
    if (isBatchTypesLoading) {
      // Prevent submission if batch types are still loading
      toastTimeout(true, "error", "Please wait for product types to load");
      return;
    }

    if (!batchTypes || batchTypes.length === 0) {
      // Prevent submission if no batch types available
      toastTimeout(
        true,
        "error",
        "No product types available. Please subscribe to products first."
      );
      return;
    }

    const validate = [
      checkBatchName(),
      checkBatchType(),
      checkFileUpload(),
    ].every(Boolean);
    if (!validate) {
      return;
    }
    setLoading(true);
    try {
      const newFormData = new FormData();

      newFormData.append("batch_type", formData.batchType || "");
      newFormData.append("batch_name", formData.batchName || "");
      newFormData.append("upload_file", formData?.batchFile || "");
      newFormData.append(
        "response_type",
        allowedProductForResponseType.includes(formData.batchType)
          ? formData.batchType === allowedProductForResponseType[1]
            ? responseCategory[1]
            : responseCategory[0]
          : responseCategory[2]
      );

      const data: any = await callAxiosPostApi(
        batchPaths.createTask,
        newFormData
      );
      const created = parseCustomerCreateTaskResponse(data);
      if (created.success) {
        onHide();
        toastTimeout(true, "success", created.message || "Batch created");
        props.getTaskData && props.getTaskData();
      } else {
        toastTimeout(true, "error", created.message || "Something went wrong");
      }
    } catch (error: any) {
      const created = parseCustomerCreateTaskResponse(error);
      toastTimeout(
        true,
        "error",
        created.message || error?.message || "Error while Creating Task"
      );
    } finally {
      setLoading(false); // Ensure loading is always set to false
    }
  };

  const downloadSampleLink = batchTypes.length
    ? batchTypes.filter((item) => item?.value === formData?.batchType)[0]
        ?.sample_link
    : "";

  return (
    <>
      {showModal && (
        <PortalOverlay>
        <div className={styles.modalOverlay} role="dialog" aria-modal="true">
          <div className={styles.modalContent}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <p
                style={{
                  fontSize: "24px",
                  fontWeight: 700,
                  margin: 0,
                  color: "#000000",
                }}
              >
                Add Batch
              </p>
              <div
                {...testIdProps("batch-service-close")}
                className={styles.crossBtn}
                onClick={onHide}
                style={{ cursor: "pointer" }}
              >
                <CrossSvg />
              </div>
            </div>

            <div className={styles.modalBody}>
              {isBatchTypesLoading ? ( // Show loading indicator while batch types are fetching
                <div className="text-center mt-5 mb-5">
                  <Loading
                    loadingColor={ACCENTCOLOR}
                    width="20px"
                    height="20px"
                  />
                  <p>Loading product types...</p>
                </div>
              ) : batchTypes.length > 0 ? ( // Only render form if batch types are loaded and available
                <>
                  <div>
                    <label className={styles.formLabel} htmlFor="batchName">
                      Batch Name
                    </label>
                    <Input
                      noshadow="true"
                      type="text"
                      name="batchName"
                      id="batch-service-batch-name"
                      autoComplete="off"
                      placeholder="Batch Name"
                      labelname=""
                      error={error?.batchName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const { value } = e.target;
                        setFormData((prev: any) => ({
                          ...prev,
                          batchName: value,
                        }));
                        setError((error) => ({
                          ...error,
                          batchName: "",
                        }));
                      }}
                      value={formData.batchName || ""} // Controlled component: bind value to state
                      customInputClass={"border-radius-10"}
                    />
                    {error.batchName && (
                      <ErrorMessage>{error.batchName}</ErrorMessage>
                    )}
                  </div>
                  <div className="mt-4">
                    <label
                      className={styles.formLabel}
                      htmlFor="z-batch-product-type"
                    >
                      Product Type
                    </label>
                    <InputDropdown
                      id="batch-service-product-type"
                      no_shadow="true"
                      hasError={error.batchType}
                      isValid={true}
                      labelname=""
                      placeholder="Select Option"
                      optionsArray={batchTypes.map((item: any) => item?.name)}
                      style={{ zIndex: "99" }}
                      click={(value: string) => {
                        const option = batchTypes.filter(
                          (item) => item?.name === value
                        );
                        setFormData((prev: any) => ({
                          ...prev,
                          batchType: option[0]?.value,
                          batchTypeName: option[0]?.name, // Store display name for default_value
                        }));
                        setError((error) => ({
                          ...error,
                          batchType: "",
                        }));
                      }}
                      default_value={formData.batchTypeName || ""} // Set default_value based on selected batchTypeName
                      className="border-radius-10"
                    />
                    {error.batchType && (
                      <ErrorMessage>{error.batchType}</ErrorMessage>
                    )}
                  </div>

                  {formData.batchFile ? (
                    <>
                      <div className={styles.fileUploaded}>
                        <p className={"mb-0 " + styles.uploadFile}>
                          File Uploaded
                        </p>
                        <div className={styles.uploadedContainer}>
                          <span>{formData?.batchFile?.name}</span>
                          <p
                            className="mb-0"
                            onClick={onFileRemove}
                            id="add-batch-remove"
                          >
                            Remove
                          </p>
                        </div>
                        {error.batchFile && (
                          <ErrorMessage>{error.batchFile}</ErrorMessage>
                        )}
                      </div>

                      <div className="d-flex justify-content-center mt-5">
                        <Button
                          size={MEDIUM}
                          hoveredStyle={primaryButtonHoverStyle}
                          style={primaryButtonStyle}
                          isLoading={loading}
                          disabled={loading}
                          type="submit"
                          onClick={() => {
                            onFormSubmit();
                          }}
                          id="batch-service-submit-batch"
                        >
                          {loading ? "Submitting..." : "Submit Batch"}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div
                      onDragOver={allowDrop}
                      onDrop={(ev: any) => {
                        onFileDrop(ev);
                      }}
                      onDragLeave={() => {
                        onDragLeave();
                      }}
                    >
                      <div className="d-flex justify-content-between mt-4">
                        <div>
                          <p className={"mb-0 " + styles.uploadFile}>
                            Upload File
                          </p>
                          <p className={styles.instructionText}>
                            CSV Format Only | Max size 100 MB
                          </p>
                        </div>
                        <div className="d-flex align-items-end">
                          {downloadSampleLink && (
                            <a
                              {...testIdProps("batch-service-download-sample")}
                              href={downloadSampleLink}
                              className={styles.downloadFile}
                              download
                            >
                              Download Sample CSV
                            </a>
                          )}
                        </div>
                      </div>
                      <div className={styles.chooseFile}>
                        <input
                          {...testIdProps("batch-service-upload")}
                          type="file"
                          className="custom-file-input d-none"
                          accept=".csv"
                          ref={fileRef}
                          onChange={(e: any) => {
                            setFormData((prev: any) => ({
                              ...prev,
                              batchFile: e.target?.files[0],
                            }));
                            setError((error) => ({
                              ...error,
                              batchFile: "",
                            }));
                          }}
                        />

                        <div className="d-flex justify-content-center">
                          <FileUploadSvg fill="#15803D" />
                        </div>
                        <p
                          style={{
                            fontSize: "14px",
                            textAlign: "center",
                            marginTop: "10px",
                          }}
                        >
                          Drag and drop file here
                        </p>

                        <div id="fileSelectEle">
                          <p
                            style={{
                              fontFamily: "Outfit, sans-serif",
                              fontStyle: "normal",
                              fontWeight: 400,
                              fontSize: "14px",
                              lineHeight: "18px",
                              textAlign: "center",
                              color: "#6B7280",
                            }}
                          >
                            Or
                          </p>
                          <div className="d-flex justify-content-center">
                            <Button
                              size={MEDIUM}
                              hoveredStyle={primaryButtonHoverStyle}
                              style={primaryButtonStyle}
                              isLoading={false}
                              type="submit"
                              htmlFor="createTaskFileeInput"
                              onClick={() => {
                                fileRef?.current?.click();
                              }}
                              id="batch-service-browse-file"
                            >
                              Browse File
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                // Show this message only if batch types are NOT loading and there are NO batch types
                <div className="text-center mt-5 mb-5">
                  Please subscribe the products first.
                </div>
              )}
            </div>
          </div>
        </div>
        </PortalOverlay>
      )}
    </>
  );
}

export default AddBatchModal;